'use client';

import { useEffect, useState } from 'react';
import { useGeneralSettings } from '@/sdk/admin/features/general_settings';
import { getTenantSlug, getCookie, getBrokerageNameFromHostname } from '@/sdk/shared/auth/utils';
import { useAuth } from '@/contexts/AuthContext';
import { getApiBaseUrl } from '@/lib/env';

export function TenantFavicon() {
  const { user } = useAuth();
  const [tenantSlug, setTenantSlug] = useState<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const resolveSlug = () => {
      const params = new URLSearchParams(window.location.search);
      // Extract root brokerage name from main domain (e.g. legacyglobalbank from devlegacyglobalbank.legacyglobalbank.com)
      const hostBrokerageName = getBrokerageNameFromHostname();

      const slug =
        user?.tenant?.slug ||
        params.get('tenantSlug') ||
        params.get('slug') ||
        getCookie('tenant_slug') ||
        getTenantSlug() ||
        localStorage.getItem('last_tenant_slug') ||
        hostBrokerageName ||
        '';

      setTenantSlug(slug);
    };

    resolveSlug();
  }, [user?.tenant?.slug]);

  const { generalSettings } = useGeneralSettings(tenantSlug || undefined);

  useEffect(() => {
    if (typeof window === 'undefined' || !tenantSlug) return;

    const fallbackUrl = `/api/admin/general-settings/logo?tenantSlug=${encodeURIComponent(tenantSlug)}`;
    const apiBaseUrl = getApiBaseUrl();
    const logoUrl = generalSettings?.logoUrl || (apiBaseUrl && apiBaseUrl !== '/api' ? `${apiBaseUrl.replace(/\/api$/, '')}${fallbackUrl}` : fallbackUrl);

    // CRITICAL: Do NOT call el.remove() on existing link elements!
    // Next.js 15 / React 19 manages metadata links as HostHoistable fibers in <head>.
    // Calling el.remove() leaves the fiber with parentNode = null, causing:
    // "Cannot read properties of null (reading 'removeChild')" on route transitions.
    // Instead, update existing link hrefs in-place.
    const isSvg = logoUrl.toLowerCase().includes('.svg');

    const existingIcons = document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']");
    if (existingIcons.length > 0) {
      existingIcons.forEach((el) => {
        el.href = logoUrl;
        if (isSvg) {
          el.type = 'image/svg+xml';
        }
      });
    } else {
      let dynamicIcon = document.getElementById('dynamic-tenant-favicon') as HTMLLinkElement | null;
      if (!dynamicIcon) {
        dynamicIcon = document.createElement('link');
        dynamicIcon.id = 'dynamic-tenant-favicon';
        dynamicIcon.rel = 'icon';
        document.head.appendChild(dynamicIcon);
      }
      dynamicIcon.href = logoUrl;
    }

    const existingAppleIcons = document.querySelectorAll<HTMLLinkElement>("link[rel*='apple-touch-icon']");
    if (existingAppleIcons.length > 0) {
      existingAppleIcons.forEach((el) => {
        el.href = logoUrl;
      });
    } else {
      let dynamicApple = document.getElementById('dynamic-tenant-apple-icon') as HTMLLinkElement | null;
      if (!dynamicApple) {
        dynamicApple = document.createElement('link');
        dynamicApple.id = 'dynamic-tenant-apple-icon';
        dynamicApple.rel = 'apple-touch-icon';
        document.head.appendChild(dynamicApple);
      }
      dynamicApple.href = logoUrl;
    }

    // Create or update dynamic <link rel="manifest"> containing tenant slug
    let manifestLink = document.querySelector<HTMLLinkElement>("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = `/manifest.webmanifest?tenantSlug=${encodeURIComponent(tenantSlug)}`;
  }, [generalSettings?.logoUrl, tenantSlug]);

  return null;
}
